/**
 * useRecording - Unified recording state management hook
 * 
 * Manages the complete recording lifecycle:
 * - idle → starting → recording → stopping → processing → preview
 * 
 * Listens to record-started/record-stopped events from:
 * - app.base.ts (for WebXR apps)
 * - VideoRecorderController (for 8th Wall apps)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import emitter from '@webcore/core/events/eventemitter';
import appEvents from '@core/events/events';
import type { MediaRecorderResponse } from '@webcore/core/mediarecorder/mediarecorder.types';

export type RecordingState = 'idle' | 'starting' | 'recording' | 'stopping' | 'processing' | 'preview';

export interface UseRecordingOptions {
  /** Max recording duration in ms (default: 30000) */
  maxDuration?: number;
  /** Called when recording starts successfully */
  onRecordingStart?: () => void;
  /** Called when recording stops */
  onRecordingStop?: () => void;
  /** Called when video is ready for preview */
  onVideoReady?: (url: string) => void;
  /** Called on any recording error */
  onError?: (error: string) => void;
}

export interface UseRecordingReturn {
  /** Current recording state */
  state: RecordingState;
  /** Recording progress (0-100) */
  progress: number;
  /** Video URL when in preview state */
  videoUrl: string | null;
  /** Last known recording aspect ratio (width/height) */
  videoAspectRatio: number | null;
  /** Start recording */
  startRecording: () => void;
  /** Stop recording */
  stopRecording: () => void;
  /** Close preview and reset to idle */
  closePreview: () => void;
  /** Convenience booleans */
  isIdle: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  isPreview: boolean;
}

export function useRecording(options: UseRecordingOptions = {}): UseRecordingReturn {
  const {
    maxDuration = 30000,
    onRecordingStart,
    onRecordingStop,
    onVideoReady,
    onError,
  } = options;

  const [state, setState] = useState<RecordingState>('idle');
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);
  
  const startTimeRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup progress interval
  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Start progress timer
  const startProgressTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    setProgress(0);
    
    progressIntervalRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      
      const elapsed = Date.now() - startTimeRef.current;
      const percentage = Math.min((elapsed / maxDuration) * 100, 100);
      
      if (percentage >= 100) {
        setProgress(100);
        clearProgressInterval();
        // Auto-stop at max duration
        emitter.emit('record-stop');
        setState('stopping');
      } else {
        setProgress(percentage);
      }
    }, 50);
  }, [maxDuration, clearProgressInterval]);

  // Actions
  const startRecording = useCallback(() => {
    if (state !== 'idle') return;
    setVideoAspectRatio(null);
    setState('starting');
    emitter.emit('record-start');
  }, [state]);

  const stopRecording = useCallback(() => {
    if (state !== 'recording') return;
    clearProgressInterval();
    setState('stopping');
    emitter.emit('record-stop');
  }, [state, clearProgressInterval]);

  const closePreview = useCallback(() => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoUrl(null);
    setVideoAspectRatio(null);
    setProgress(0);
    setState('idle');
    appEvents.emit('preview-close');
    appEvents.emit('content-resume');
  }, [videoUrl]);

  // Use refs for callbacks to avoid effect re-runs when callbacks change
  const onRecordingStartRef = useRef(onRecordingStart);
  const onRecordingStopRef = useRef(onRecordingStop);
  const onVideoReadyRef = useRef(onVideoReady);
  const onErrorRef = useRef(onError);
  
  // Keep refs updated
  useEffect(() => {
    onRecordingStartRef.current = onRecordingStart;
    onRecordingStopRef.current = onRecordingStop;
    onVideoReadyRef.current = onVideoReady;
    onErrorRef.current = onError;
  });

  // Event handlers
  useEffect(() => {
    const handleRecordStarted = (result: MediaRecorderResponse | Error | { success: boolean; error?: string }) => {
      // Handle errors
      if (result instanceof Error) {
        console.error('[useRecording] Recording start error:', result.message);
        setState('idle');
        onErrorRef.current?.(result.message);
        return;
      }
      
      if (result.error || (result as any).success === false) {
        const errorMsg = result.error || 'Failed to start recording';
        console.error('[useRecording] Recording start error:', errorMsg);
        setState('idle');
        onErrorRef.current?.(errorMsg);
        return;
      }
      
      // Success - start recording
      setState('recording');
      startProgressTimer();
      onRecordingStartRef.current?.();
    };

    const handleRecordStopped = (result: MediaRecorderResponse | Error | { success: boolean; error?: string; url?: string }) => {
      clearProgressInterval();
      
      // Handle errors
      if (result instanceof Error) {
        console.error('[useRecording] Recording stop error:', result.message);
        setState('idle');
        setProgress(0);
        onErrorRef.current?.(result.message);
        return;
      }
      
      if (result.error || (result as any).success === false) {
        const errorMsg = result.error || 'Failed to stop recording';
        console.error('[useRecording] Recording stop error:', errorMsg);
        setState('idle');
        setProgress(0);
        onErrorRef.current?.(errorMsg);
        return;
      }
      
      // Success - show preview if we have a URL
      onRecordingStopRef.current?.();
      
      if (result.url) {
        setVideoUrl(result.url);
        setState('preview');
        appEvents.emit('content-stop');
        onVideoReadyRef.current?.(result.url);
      } else {
        setState('idle');
        setProgress(0);
        setVideoAspectRatio(null);
      }
    };
    const handleRecordAspectRatio = (result: { aspectRatio?: number } | number | null | undefined) => {
      const aspectRatio = typeof result === 'number' ? result : result?.aspectRatio;
      if (typeof aspectRatio !== 'number' || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
        return;
      }
      setVideoAspectRatio(aspectRatio);
    };

    emitter.on('record-started', handleRecordStarted);
    emitter.on('record-stopped', handleRecordStopped);
    emitter.on('record-aspect-ratio', handleRecordAspectRatio);

    return () => {
      emitter.off('record-started', handleRecordStarted);
      emitter.off('record-stopped', handleRecordStopped);
      emitter.off('record-aspect-ratio', handleRecordAspectRatio);
      clearProgressInterval();
    };
  }, [startProgressTimer, clearProgressInterval]);

  return {
    state,
    progress,
    videoUrl,
    videoAspectRatio,
    startRecording,
    stopRecording,
    closePreview,
    // Convenience booleans
    isIdle: state === 'idle',
    isRecording: state === 'recording',
    isProcessing: state === 'stopping' || state === 'processing',
    isPreview: state === 'preview',
  };
}

export default useRecording;
